import React, { useCallback, useContext, useMemo, useState } from "react";
import { Grid as GridBase, Button, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";

import ConfirmLocationBase from "./extend-confirmation/ConfirmLocation";
import ConfirmLocationTotalBase from "./extend-confirmation/ConfirmLocationTotal";
import { GlobalContext, LoaderContext } from "@/helpers/contexts/contexts";
import { campaignService } from "@/features/campaigns/shared/services/campaignService";
import { stripeMicroService } from "@/shared/services/stripeMicroService";
import { logger } from "@/helpers/loggerHelper";
import ConfirmStripeEmbedCheckoutPopupBase from "@/components/stripe/embedCheckout/ConfirmStripeEmbedCheckoutPopup";
import { formatDateForApiRequest } from "@/helpers/utils";
import { ROUTES } from "@/helpers/routes/routePaths";
import { grantThankYouAccessForFlow } from "@/shared/helpers/thankYouAccessHelper";

type Id = number | string;

type ExtendSelection = {
  location_id: Id;
  location_name?: string;
  extend_budget?: number | string;
  extend_start_date?: string;
  extend_end_date?: string;
  extend_creatives: Id[];
  locationInfo: {
    system_generated_location_id?: string;
    packages_data?: unknown[];
  };
  [key: string]: unknown;
};

type ExtendCampaignCreative = {
  id: Id;
  package_name?: string;
  channels?: Array<{
    creative_id?: Id;
  }>;
};

type ExtendCampaignData = {
  id?: Id;
  campaign_name?: string;
  campaign_type?: string;
  system_generated_campaign_id?: string;
  enable_payment_through_portal?: number;
  campaign_creatives: ExtendCampaignCreative[];
};

type ExtendConfirmationProps = {
  selections: ExtendSelection[];
  onMakeChanges?: () => void;
  campaignData?: ExtendCampaignData;
};

type InvoicePayload = Record<string, unknown>;

type ExtendUser = {
  id?: Id;
  email?: string;
  first_name?: string;
  last_name?: string;
  participantData?: {
    id?: Id;
    system_generated_participant_id?: string;
  };
};

type ExtendBrand = {
  id?: Id;
  brand_name?: string;
  system_generated_brand_id?: string;
};

const ConfirmLocation = ConfirmLocationBase as React.ComponentType<
  Record<string, unknown>
>;

const ConfirmLocationTotal = ConfirmLocationTotalBase as React.ComponentType<
  Record<string, unknown>
>;

const Grid = GridBase as React.ComponentType<
  Record<string, unknown> & { children?: React.ReactNode }
>;

const ConfirmStripeEmbedCheckoutPopup =
  ConfirmStripeEmbedCheckoutPopupBase as unknown as React.ComponentType<
    Record<string, unknown>
  >;

function ExtendConfirmation({
  selections,
  onMakeChanges,
  campaignData,
}: ExtendConfirmationProps) {
  const navigate = useNavigate();
  const [openPayment, setOpenPayment] = useState(false);
  const { loggedInUser, selectedBrand } = useContext(GlobalContext) as {
    loggedInUser?: ExtendUser;
    selectedBrand?: ExtendBrand;
  };
  const loader = useContext(LoaderContext);

  const totalBudget = useMemo(() => {
    return selections?.reduce((t, l) => t + Number(l?.extend_budget), 0);
  }, [selections]);

  const submitInvoice = useCallback(
    async (data: InvoicePayload) => {
      try {
        loader(true);
        const response =
          await campaignService.saveCampaignExtensionBudget(data);
        if (response.status === 200) {
          grantThankYouAccessForFlow({
            queryType: "extend_campaign",
            stateFrom: "extend_campaign",
          });
          navigate(`${ROUTES.THANK_YOU}?type=extend_campaign`, {
            replace: true,
            state: {
              from: "extend_campaign",
            },
          });
        }
      } catch (error) {
        const err = error as {
          response?: {
            data?: unknown;
          };
        };
        logger.error("submitInvoice error", err.response?.data);
      } finally {
        loader(false);
      }
    },
    [navigate, loader]
  );

  const dataToSave = useCallback(
    (payment_status = 0) => {
      const locationData = selections.map((l) => {
        const creativeList =
          campaignData?.campaign_creatives.filter((c) =>
            l.extend_creatives.includes(c.id)
          ) || [];
        const formattedCreativeData = creativeList.map((c) => {
          const channelData = c.channels?.[0]; // taking first channel

          return {
            creative_id: c.id,
            creative_name: c?.package_name, // from channels
            sys_creative_id: channelData?.creative_id,
          };
        });

        return {
          amount: Number(l?.extend_budget),
          budget: Number(l?.extend_budget),
          end_date: l?.extend_end_date,
          creatives: l.extend_creatives,
          locationId: l.location_id,
          start_date: formatDateForApiRequest(l.extend_start_date),
          creativeData: formattedCreativeData,
          locationName: l?.location_name,
          brand_contribution_amount: 0,
          system_generated_location_id:
            l.locationInfo.system_generated_location_id,
        };
      });

      return {
        id: campaignData?.id,
        fk_campaign_id: campaignData?.id,
        system_generated_campaign_id:
          campaignData?.system_generated_campaign_id,
        fk_campaign_name: campaignData?.campaign_name,
        campaign_type: campaignData?.campaign_type,
        email_id: loggedInUser?.email,
        user_id: loggedInUser?.id,
        user_name: `${loggedInUser?.first_name} ${loggedInUser?.last_name}`,
        system_generated_participant_id:
          loggedInUser?.participantData?.system_generated_participant_id,
        fk_participant_id: loggedInUser?.participantData?.id,
        fk_brand_id: selectedBrand?.id,
        system_generated_brand_id: selectedBrand?.system_generated_brand_id,
        fk_brand_name: selectedBrand?.brand_name,
        locationData: JSON.stringify(locationData),
        payment_status: payment_status,
        total_budget: totalBudget,
        enable_payment_through_portal:
          campaignData?.enable_payment_through_portal || 0,
      };
    },
    [campaignData, loggedInUser, selectedBrand, selections, totalBudget]
  );

  const handleOnFinalConfirm = useCallback(() => {
    campaignData?.enable_payment_through_portal
      ? setOpenPayment(true)
      : submitInvoice(dataToSave(0));
  }, [campaignData?.enable_payment_through_portal, submitInvoice, dataToSave]);

  return (
    <div className="my-4">
      {selections?.map((l) => (
        <ConfirmLocation
          key={l?.location_id}
          data={l}
          campaign_creatives={l?.locationInfo?.packages_data}
        />
      ))}

      <ConfirmLocationTotal totalBudget={totalBudget} />
      <Grid container spacing={2} className="mt-5">
        <Grid item size={{ md: "grow" }}>
          <Typography variant="caption" className="text-right">
            Once 'Confirm' is selected, you can't make changes, your selections
            will be final for this campaign
          </Typography>
        </Grid>
        <Grid item size={{ xs: 12, md: "auto" }}>
          <Grid container spacing={2}>
            <Grid item size={{ xs: 12, md: 6 }}>
              <Button
                variant="outlined"
                color="primary"
                className="px-2"
                onClick={onMakeChanges}
                fullWidth
              >
                Make Changes
              </Button>
            </Grid>
            <Grid item size={{ xs: 12, md: 6 }}>
              <Button
                variant="contained"
                color="primary"
                className="px-5"
                fullWidth
                onClick={handleOnFinalConfirm}
              >
                Confirm
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {openPayment ? (
        <ConfirmStripeEmbedCheckoutPopup
          makePayment={openPayment}
          title="Payment"
          data={{
            email: loggedInUser?.email,
            amount: totalBudget,
            fk_brand_id: selectedBrand?.id,
            system_generated_brand_id: selectedBrand?.system_generated_brand_id,
            fk_brand_name: selectedBrand?.brand_name,
            ...dataToSave(1),
          }}
          onClose={() => setOpenPayment(false)}
          return_uri={`/extend-campaign-return?extend_campaign=true`}
          api={stripeMicroService.extendCampaignPayment}
        />
      ) : null}
    </div>
  );
}

export default ExtendConfirmation;
